import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../../redux/store'
import { createProductApi, getProductByIdApi, updateProductApi } from '../../../../../redux/slices/ProductSlice'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

const ProductForm = () => {
    const { id } = useParams()
    const dispatch = useDispatch<AppDispatch>()
    const [loading, setLoading] = useState(false)
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const navigate = useNavigate()

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        sku: Yup.string().required('SKU is required'),
        price: Yup.number().required('Price is required').min(0, 'Price must be positive'),
        stock: Yup.number().required('Stock is required').min(0, 'Stock must be non-negative'),
    })

    // Formik configuration
    const formik = useFormik({
        initialValues: {
            name: '',
            sku: '',
            price: '',
            stock: '',
            description: '',
            product_image: null as File | null,
            is_active: 1,
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true)
            const formData = new FormData()
            formData.append('name', values.name)
            formData.append('sku', values.sku)
            formData.append('price', values.price.toString())
            formData.append('stock', values.stock.toString())
            formData.append('description', values.description)
            formData.append('is_active', values.is_active.toString())

            if (values.product_image) {
                formData.append('product_image', values.product_image)
            }

            let data
            if (id) {
                // Update product API call
                data = await dispatch(updateProductApi({ id, payload: formData }))
            } else {
                // Create new product API call
                data = await dispatch(createProductApi(formData))
            }

            if (data.payload?.success) {
                setLoading(false)
                toast.success(data.payload.reason)
                navigate(-1)
            } else {
                setLoading(false)
                toast.error(data.payload?.reason || 'Something went wrong')
            }
        },
    })

    useEffect(() => {
        if (id) {
            fetchProductData()
        }
    }, [id])

    const fetchProductData = async () => {
        dispatch(getProductByIdApi(id))
            .then((response) => {
                if (response.payload) {
                    const fetchedData = response.payload?.data || response.payload
                    console.log(fetchedData)
                    formik.setValues({
                        ...fetchedData,
                        product_image: null, // Keep this null to handle file inputs correctly
                        price: fetchedData.price || '',
                        stock: fetchedData.stock || '',
                    })

                    const imageUrl = fetchedData.product_image_url || fetchedData.product_image
                    if (imageUrl) {
                        setImagePreview(imageUrl)
                    } else {
                        setImagePreview(null)
                    }
                } else {
                    toast.error('Failed to fetch product data')
                }
            })
            .catch(() => {
                toast.error('An error occurred while fetching data')
            })
    }

    return (
        <div className='container mt-4'>
            <div className='card shadow' style={{ height: 'auto', minHeight: '500px' }}>
                <form onSubmit={formik.handleSubmit}>
                    {/* Card Header */}
                    <div className='card-header bg-light d-flex justify-content-between align-items-center position-relative py-6'>
                        <h2 className='mb-0'>{id ? 'Edit Product' : 'Add Product'}</h2>
                        <button type='submit' className='btn btn-primary' disabled={loading}>
                            {loading ? 'Saving...' : id ? 'Update' : 'Submit'}
                        </button>
                        <span
                            className={`position-absolute border-primary`}
                            style={{
                                top: '12px',
                                left: '0',
                                height: '70%',
                                width: '4px',
                                backgroundColor: 'var(--bs-primary)',
                            }}
                        ></span>
                    </div>

                    {/* Card Body */}
                    <div className='card-body p-6'>
                        <div className='row g-3'>
                            {/* Name */}
                            <div className='col-md-6'>
                                <label htmlFor='name' className='form-label'>
                                    Name <span className='text-danger'>*</span>
                                </label>
                                <input
                                    type='text'
                                    id='name'
                                    name='name'
                                    className='form-control'
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder='Enter product name'
                                />
                                {formik.touched.name && formik.errors.name && (
                                    <div className='text-danger'>{formik.errors.name}</div>
                                )}
                            </div>

                            {/* SKU */}
                            <div className='col-md-6'>
                                <label htmlFor='sku' className='form-label'>
                                    SKU <span className='text-danger'>*</span>
                                </label>
                                <input
                                    type='text'
                                    id='sku'
                                    name='sku'
                                    className='form-control'
                                    value={formik.values.sku}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder='Enter SKU'
                                />
                                {formik.touched.sku && formik.errors.sku && (
                                    <div className='text-danger'>{formik.errors.sku}</div>
                                )}
                            </div>

                            {/* Price */}
                            <div className='col-md-6'>
                                <label htmlFor='price' className='form-label'>
                                    Price <span className='text-danger'>*</span>
                                </label>
                                <input
                                    type='number'
                                    id='price'
                                    name='price'
                                    className='form-control'
                                    value={formik.values.price}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder='Enter price'
                                />
                                {formik.touched.price && formik.errors.price && (
                                    <div className='text-danger'>{formik.errors.price}</div>
                                )}
                            </div>

                            {/* Stock */}
                            <div className='col-md-6'>
                                <label htmlFor='stock' className='form-label'>
                                    Stock <span className='text-danger'>*</span>
                                </label>
                                <input
                                    type='number'
                                    id='stock'
                                    name='stock'
                                    className='form-control'
                                    value={formik.values.stock}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder='Enter stock quantity'
                                />
                                {formik.touched.stock && formik.errors.stock && (
                                    <div className='text-danger'>{formik.errors.stock}</div>
                                )}
                            </div>

                            {/* Status */}
                            <div className='col-md-6'>
                                <label htmlFor='is_active' className='form-label'>
                                    Status
                                </label>
                                <select
                                    id='is_active'
                                    name='is_active'
                                    className='form-select'
                                    value={formik.values.is_active}
                                    onChange={(e) => {
                                        formik.setFieldValue('is_active', parseInt(e.target.value))
                                    }}
                                >
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </select>
                            </div>

                            {/* Product Image */}
                            <div className='col-md-6'>
                                <label htmlFor='product_image' className='form-label'>
                                    Product Image
                                </label>
                                <input
                                    type='file'
                                    id='product_image'
                                    name='product_image'
                                    className='form-control'
                                    accept='image/*'
                                    onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                            formik.setFieldValue('product_image', e.target.files[0])
                                            setImagePreview(URL.createObjectURL(e.target.files[0]))
                                        }
                                    }}
                                />
                                {imagePreview && (
                                    <div className='mt-3'>
                                        <img
                                            src={imagePreview}
                                            alt='Preview'
                                            className='img-thumbnail'
                                            style={{ maxWidth: '200px' }}
                                        />
                                        <button
                                            type='button'
                                            className='btn btn-sm btn-danger ms-3'
                                            onClick={() => {
                                                setImagePreview(null)
                                                formik.setFieldValue('product_image', null)
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Description */}
                            <div className='col-12'>
                                <label htmlFor='description' className='form-label'>
                                    Description
                                </label>
                                <textarea
                                    id='description'
                                    name='description'
                                    className='form-control'
                                    rows={4}
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    placeholder='Enter product description'
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default ProductForm
